import { effect, Injectable, Signal, signal } from '@angular/core';
import { ThemePreference } from '@shared/types/theme.type';
import { StorageKey } from '@app/core/storage/storage-key.enum';
import { environment } from '@environments/environment';
import { LocalStorageService } from '@app/core/storage/local-storage.service';

@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    private _theme = signal<ThemePreference>(this._initialTheme());
    private readonly _mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    constructor(private storage: LocalStorageService) {
        effect(() => {
            if (environment.dynamicTheme && this._theme()) {
                this._toggleTheme();
                if (this._theme() === 'system') {
                    this._mediaQuery.addEventListener('change', () => this._toggleTheme());
                } else {
                    this._mediaQuery.removeEventListener('change', () => this._toggleTheme());
                }
            }
        });
    }

    get theme(): Signal<ThemePreference> {
        return this._theme.asReadonly();
    }

    set theme(value: ThemePreference) {
        if (value) {
            this.storage.saveItem<ThemePreference>(StorageKey.theme, value);
            this._theme.set(value);
        }
    }

    init(): void {
        if (environment.dynamicTheme) {
            this._toggleTheme();
        }
    }

    private _toggleTheme(): void {
        const isDark = this._theme() === 'dark';
        const isSystem = this._theme() === 'system' && this._mediaQuery.matches;
        const element = document.querySelector('html');
        element.classList.toggle('dark', isDark || isSystem);
    }

    private _initialTheme(): ThemePreference {
        return this.storage.hasItem(StorageKey.theme) ? this.storage.getItem<ThemePreference>(StorageKey.theme) : 'light';
    }
}
